import { useCallback } from 'react'

import type { ScaRetryParameters, ScaRetrySession } from './useScaRetryState'
import { useScaRetryState } from './useScaRetryState'
import type {
  PaymentActionRequired,
  ScheduledSubscriptionUpdateV2,
} from '../gql/graphql'
import { SubscriptionUpdateStatus } from '../gql/graphql'

export const SCA_PAYMENT_ERROR_NAME = 'ScaPaymentError'

function makeScaError(message: string): Error {
  const err = new Error(message)
  err.name = SCA_PAYMENT_ERROR_NAME
  return err
}

/**
 * Result from subscription update mutations.
 * Matches the discriminated union pattern from entities package.
 */
export type SubscriptionUpdateResult =
  | {
      status: SubscriptionUpdateStatus.Success
      scheduledUpdate?: ScheduledSubscriptionUpdateV2
    }
  | {
      status: SubscriptionUpdateStatus.RequiresAction
      paymentAction: PaymentActionRequired
      scheduledUpdate?: ScheduledSubscriptionUpdateV2
    }

/**
 * Phase tracking for subscription update flows.
 * Helps provide user feedback during the update process.
 */
export enum UpdatePlanPhase {
  Idle = 'idle',
  Updating = 'updating', // Making API call to update subscription
  Authorizing = 'authorizing', // User completing 3D Secure authentication
  Confirming = 'confirming', // Confirming successful update
}

/**
 * Stripe payment confirmation hook interface.
 * Expected shape from useStripePaymentConfirmation hook.
 */
export interface PaymentConfirmationHook {
  confirmPayment: (
    clientSecret: string,
    paymentMethodId?: string,
  ) => Promise<{ success: boolean }>
  isConfirming: boolean
  isFeatureEnabled: boolean
}

/**
 * Parameters for executing a subscription update.
 * Generic to support different update types (plan, quantity, etc.).
 */
export interface SubscriptionUpdateParams<TPlanId, TInterval> {
  planId: TPlanId
  interval: TInterval
  quantity: number
  [key: string]: any // Allow additional parameters
}

/**
 * Callbacks for subscription update lifecycle events.
 */
export interface SubscriptionUpdateCallbacks<TSuccessData> {
  /** Called when update phase changes (for UI feedback) */
  onPhaseChange?: (phase: UpdatePlanPhase) => void

  /** Called when payment succeeds and subscription is updated */
  onSuccess: (data: TSuccessData) => void | Promise<void>

  /** Called when update fails */
  onError?: (error: Error) => void
}

/**
 * Configuration for useScaSubscriptionUpdate hook.
 */
export interface UseScaSubscriptionUpdateConfig<
  TPlanId,
  TInterval,
  TUpdateParams extends SubscriptionUpdateParams<TPlanId, TInterval>,
  TSuccessData,
> {
  /** Hook that provides Stripe payment confirmation functionality */
  paymentConfirmationHook: PaymentConfirmationHook

  /** Function to call API and update subscription */
  updateFn: (params: TUpdateParams) => Promise<SubscriptionUpdateResult>

  /** Optional function to refetch data after successful update */
  refetch?: () => Promise<any>

  /** Lifecycle callbacks */
  callbacks: SubscriptionUpdateCallbacks<TSuccessData>
}

/**
 * Custom hook for managing SCA (Strong Customer Authentication) subscription updates.
 *
 * Handles the complete flow for subscription updates that may require Stripe 3D Secure:
 * - **SCENARIO A**: Pure retry with saved client secret (when user retries after failed 3DS)
 * - **SCENARIO B**: New attempt or parameters changed (makes new API call)
 *
 * This hook centralizes ~150 lines of duplicated retry logic found across 4 locations
 * in the codebase, reducing it to a simple `executeUpdate()` call.
 *
 * **Architecture:**
 * - Uses `useScaRetryState` for state management (single object state per React best practices)
 * - Composable: You provide the update function, payment hook, and callbacks
 * - Type-safe: Generic parameters adapt to your specific update flow
 *
 * @template TPlanId - Type for plan identifier (e.g., ObPlanId)
 * @template TInterval - Type for billing interval (e.g., BillingInterval)
 * @template TUpdateParams - Type for update parameters (extends SubscriptionUpdateParams)
 * @template TSuccessData - Type for success callback data
 *
 * @param config - Configuration object with update function, payment hook, and callbacks
 * @returns Object with executeUpdate function and isUpdating flag
 *
 * @example
 * ```tsx
 * const { executeUpdate, isUpdating } = useScaSubscriptionUpdate({
 *   paymentConfirmationHook: useStripePaymentConfirmation(),
 *   updateFn: async (params) => await updatePlan(params),
 *   refetch: refetch,
 *   callbacks: {
 *     onPhaseChange: setUpdatePlanPhase,
 *     onSuccess: (data) => {
 *       showSuccessModal(data)
 *     },
 *     onError: (error) => {
 *       console.error(error)
 *     },
 *   },
 * })
 *
 * // Usage in component:
 * await executeUpdate({
 *   planId: 'essentials',
 *   interval: 'year',
 *   quantity: 10,
 * })
 * ```
 */
export function useScaSubscriptionUpdate<
  TPlanId = string,
  TInterval = string,
  TUpdateParams extends SubscriptionUpdateParams<TPlanId, TInterval> =
    SubscriptionUpdateParams<TPlanId, TInterval>,
  TSuccessData = any,
>(
  config: UseScaSubscriptionUpdateConfig<
    TPlanId,
    TInterval,
    TUpdateParams,
    TSuccessData
  >,
) {
  const { paymentConfirmationHook, updateFn, refetch, callbacks } = config
  const {
    confirmPayment,
    isConfirming,
    isFeatureEnabled: isScaEnabled,
  } = paymentConfirmationHook
  const { onPhaseChange, onSuccess, onError } = callbacks

  const {
    retryState,
    saveRetryData,
    clearRetryData,
    hasParametersChanged,
    canRetry,
  } = useScaRetryState<TPlanId, TInterval>()

  /**
   * Executes a subscription update with SCA retry support.
   *
   * Handles two scenarios:
   * - SCENARIO A: Pure retry (reuses saved client secret when parameters unchanged)
   * - SCENARIO B: New attempt or changed parameters (makes new API call)
   */
  const executeUpdate = useCallback(
    async (params: TUpdateParams): Promise<void> => {
      const retryParams: ScaRetryParameters<TPlanId, TInterval> = {
        planId: params.planId,
        interval: params.interval,
        quantity: params.quantity,
      }

      // Check if parameters changed since we saved the client secret
      const parametersChanged = hasParametersChanged(retryParams)

      // ============================================================
      // SCENARIO A: Pure retry - reuse client secret
      // ============================================================
      if (
        isScaEnabled &&
        canRetry() &&
        !parametersChanged &&
        retryState.session
      ) {
        onPhaseChange?.(UpdatePlanPhase.Authorizing)

        let confirmResult
        try {
          confirmResult = await confirmPayment(
            retryState.session.clientSecret,
            retryState.session.paymentMethodId ?? undefined,
          )
        } catch (error) {
          onPhaseChange?.(UpdatePlanPhase.Idle)
          const errorMessage = 'Payment authorization failed. Please try again.'
          onError?.(makeScaError(errorMessage))
          throw makeScaError(errorMessage)
        }

        if (confirmResult.success) {
          // Success - clear retry state
          clearRetryData()
          onPhaseChange?.(UpdatePlanPhase.Confirming)

          // Refetch to get updated subscription data
          if (refetch) {
            await refetch()
          }

          // Call success callback with parameters
          await onSuccess(params as unknown as TSuccessData)
          onPhaseChange?.(UpdatePlanPhase.Idle)
          return
        } else {
          // Still failed - keep retry mode active (state already has session data)
          onPhaseChange?.(UpdatePlanPhase.Idle)
          const errorMessage = 'Payment authorization failed. Please try again.'
          onError?.(makeScaError(errorMessage))
          throw makeScaError(errorMessage)
        }
      }

      // ============================================================
      // SCENARIO B: New attempt or parameters changed
      // ============================================================

      // Clear any existing retry state
      clearRetryData()

      // Set updating phase
      onPhaseChange?.(UpdatePlanPhase.Updating)

      // Execute the update
      let response: SubscriptionUpdateResult
      try {
        response = await updateFn(params)
      } catch (error) {
        onPhaseChange?.(UpdatePlanPhase.Idle)
        const errorObj =
          error instanceof Error ? error : new Error('Update failed')
        onError?.(errorObj)
        throw errorObj
      }

      // Check if payment requires additional authentication (SCA)
      if (
        isScaEnabled &&
        response?.status === SubscriptionUpdateStatus.RequiresAction &&
        response?.paymentAction?.clientSecret
      ) {
        // Save retry session for potential retry
        const session: ScaRetrySession<TPlanId, TInterval> = {
          clientSecret: response.paymentAction.clientSecret,
          paymentMethodId: response.paymentAction.paymentMethodId ?? null,
          planId: params.planId,
          interval: params.interval,
          quantity: params.quantity,
        }
        saveRetryData(session)

        // Set phase to authorizing before showing Stripe modal
        onPhaseChange?.(UpdatePlanPhase.Authorizing)

        // Trigger Stripe 3D Secure authentication
        let confirmResult
        try {
          confirmResult = await confirmPayment(
            response.paymentAction.clientSecret,
            response.paymentAction.paymentMethodId ?? undefined,
          )
        } catch (error) {
          // Unexpected error (network failure, etc.)
          onPhaseChange?.(UpdatePlanPhase.Idle)
          // Note: retry mode already enabled by saveRetryData above
          const errorMessage = 'Payment authorization failed. Please try again.'
          onError?.(new Error(errorMessage))
          throw new Error(errorMessage, { cause: error })
        }

        if (confirmResult.success) {
          // Payment succeeded - clear retry state
          clearRetryData()
          onPhaseChange?.(UpdatePlanPhase.Confirming)

          // Refetch to get updated subscription data
          if (refetch) {
            await refetch()
          }

          // Call success callback
          await onSuccess(params as unknown as TSuccessData)
          onPhaseChange?.(UpdatePlanPhase.Idle)
          return
        } else {
          // Payment failed or canceled - retry mode already enabled by saveRetryData
          onPhaseChange?.(UpdatePlanPhase.Idle)
          const errorMessage = 'Payment authorization failed. Please try again.'
          onError?.(makeScaError(errorMessage))
          throw makeScaError(errorMessage)
        }
      }

      // Normal flow - no SCA required or SCA not enabled
      if (refetch) {
        await refetch()
      }

      // Call success callback
      await onSuccess(params as unknown as TSuccessData)
      onPhaseChange?.(UpdatePlanPhase.Idle)
    },
    [
      isScaEnabled,
      canRetry,
      hasParametersChanged,
      retryState.session,
      confirmPayment,
      clearRetryData,
      saveRetryData,
      updateFn,
      refetch,
      onPhaseChange,
      onSuccess,
      onError,
    ],
  )

  return {
    executeUpdate,
    isUpdating: isConfirming,
    retryState, // Expose retry state for debugging/UI
  }
}
